import { motion } from "framer-motion";

/**
 * Pixel-faithful replica of the "Trusted 30s Quality Service — In North Telangana" badge.
 *
 * From the brand asset:
 *  - White background circle with thick coral outer ring
 *  - "TRUSTED" bold dark-blue text arcing along the top
 *  - Two coral smile-shaped curved lines in the middle (decorative)
 *  - Large "30S" centred in dark-blue (30 huge, S superscript)
 *  - Coral filled dot on the left side
 *  - "QUALITY SERVICE" bold dark-blue text arcing along the bottom
 *  - "IN NORTH TELANGANA" bold coral text below the circle
 */
export default function TrustBadge({ size = 200, animate = true }) {
  const BLUE  = "#1d3a8a";
  const CORAL = "#e8523a";

  const badge = (
    <svg
      viewBox="0 0 220 260"
      width={size}
      height={(size * 260) / 220}
      fill="none"
      aria-label="Trusted 30 years Quality Service in North Telangana"
    >
      {/* ── Outer coral ring ── */}
      <circle cx="110" cy="110" r="104" stroke={CORAL} strokeWidth="8" fill="white" />

      {/* ── Inner thin ring (subtle) ── */}
      <circle cx="110" cy="110" r="92" stroke={CORAL} strokeWidth="1.5" fill="none" opacity="0.25" />

      {/* ── Decorative coral smile arcs (middle of circle) ── */}
      {/* Top smile */}
      <path
        d="M 52 95 Q 110 62 168 95"
        stroke={CORAL} strokeWidth="4" strokeLinecap="round" fill="none"
      />
      {/* Bottom smile */}
      <path
        d="M 52 125 Q 110 158 168 125"
        stroke={CORAL} strokeWidth="4" strokeLinecap="round" fill="none"
      />

      {/* ── Coral dot on the left ── */}
      <circle cx="20" cy="110" r="6" fill={CORAL} />

      {/* ── "TRUSTED" arc along the top ── */}
      <defs>
        <path
          id="topTextArc"
          d="M 18 110 A 92 92 0 0 1 202 110"
        />
        <path
          id="botTextArc"
          d="M 18 110 A 92 92 0 0 0 202 110"
        />
      </defs>
      <text
        fontSize="19"
        fontWeight="900"
        fontFamily="Poppins, Inter, sans-serif"
        fill={BLUE}
        letterSpacing="3"
      >
        <textPath href="#topTextArc" startOffset="22%">TRUSTED</textPath>
      </text>

      {/* ── "QUALITY SERVICE" arc along the bottom ── */}
      <text
        fontSize="14"
        fontWeight="800"
        fontFamily="Poppins, Inter, sans-serif"
        fill={BLUE}
        letterSpacing="2"
      >
        <textPath href="#botTextArc" startOffset="9%">QUALITY SERVICE</textPath>
      </text>

      {/* ── "30" — giant bold numeral ── */}
      <text
        x="96"
        y="122"
        textAnchor="middle"
        fontSize="72"
        fontWeight="900"
        fontFamily="Poppins, Inter, sans-serif"
        fill={BLUE}
      >
        30
      </text>

      {/* ── "S" — superscript next to 30 ── */}
      <text
        x="162"
        y="102"
        textAnchor="middle"
        fontSize="34"
        fontWeight="900"
        fontFamily="Poppins, Inter, sans-serif"
        fill={BLUE}
      >
        S
      </text>

      {/* ── "IN NORTH TELANGANA" below the circle ── */}
      <text
        x="110"
        y="238"
        textAnchor="middle"
        fontSize="15"
        fontWeight="800"
        fontFamily="Poppins, Inter, sans-serif"
        fill={CORAL}
        letterSpacing="1.5"
      >
        IN NORTH TELANGANA
      </text>
    </svg>
  );

  if (!animate) return badge;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.06, rotate: 2 }}
    >
      {badge}
    </motion.div>
  );
}
